/**
 * /invite/:token — magic-link landing page.
 *
 * Guest path: GET previews the invitation, recipient confirms (and
 * optionally tweaks the display name), POST mints a session, we
 * refresh /me and land on Today.
 *
 * Authed path: a different account is already signed in — we don't
 * silently switch them over. Show a "sign out first" call to action;
 * the token stays valid until the recipient comes back.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

interface Preview {
  displayName: string;
  email: string;
  role: 'parent' | 'youth';
  familyName: string | null;
  inviterDisplayName: string | null;
  expiresAt: number;
  /** 'existing' = the recipient is already in the household and this
   *  link just attaches email sign-in; 'new' = joining as a new member. */
  mode: 'new' | 'existing';
}

export default function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const auth = useAuth();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    api
      .get<Preview>(`/auth/invite/${encodeURIComponent(token)}`)
      .then((p) => {
        if (!alive) return;
        setPreview(p);
        setDisplayName(p.displayName);
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setErr(
          e instanceof ApiError ? e.message : 'This invitation link is no longer valid.',
        );
      });
    return () => {
      alive = false;
    };
  }, [token]);

  async function accept() {
    if (!token || !preview) return;
    setSubmitting(true);
    setErr(null);
    try {
      await api.post(`/auth/invite/${encodeURIComponent(token)}/accept`, {
        displayName: displayName.trim() || undefined,
      });
      // /me will now reflect the new session. Refresh and let the
      // top-level router send us to /today.
      await qc.invalidateQueries({ queryKey: ['me'] });
      navigate('/today', { replace: true });
    } catch (e) {
      setErr(
        e instanceof ApiError ? e.message : 'Could not accept the invitation. Try again.',
      );
      setSubmitting(false);
    }
  }

  // No early return while auth is still resolving: the invitation is a
  // public landing driven by its own preview fetch, so we never block it
  // on the /me probe. Once auth settles to signed-in, the screen below
  // takes over; until then a (signed-out) recipient sees the card.
  if (auth.state === 'signed-in') {
    return (
      <div className="min-h-full bg-paper flex items-center justify-center p-6">
        <div className="max-w-sm text-center space-y-4">
          <h1 className="font-serif text-2xl text-ink">You're already signed in</h1>
          <p className="text-soft text-sm">
            You're signed in as {auth.me.member.displayName}. Sign out first if you'd like
            to accept this invitation on this device.
          </p>
          <button
            onClick={() => navigate('/you')}
            className="h-11 px-5 rounded-xl bg-oxblood text-paper font-medium"
          >
            Go to Settings
          </button>
        </div>
      </div>
    );
  }

  if (err && !preview) {
    return (
      <div className="min-h-full bg-paper flex items-center justify-center p-6">
        <div className="max-w-sm text-center space-y-4">
          <h1 className="font-serif text-2xl text-ink">Invitation unavailable</h1>
          <p className="text-soft text-sm">{err}</p>
          <button
            onClick={() => navigate('/')}
            className="h-11 px-5 rounded-xl bg-oxblood text-paper font-medium"
          >
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="min-h-full flex items-center justify-center bg-paper">
        <div className="text-soft text-sm">Loading invitation…</div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-paper flex items-center justify-center p-6">
      <div className="max-w-sm w-full space-y-5">
        <div className="text-center space-y-2">
          <h1 className="font-serif text-2xl text-ink">
            {preview.mode === 'existing'
              ? `Sign in to ${preview.familyName ?? 'your household'}`
              : `Join ${preview.familyName ?? 'this household'}`}
          </h1>
          <p className="text-soft text-sm">
            {preview.mode === 'existing'
              ? `${preview.inviterDisplayName ?? 'A family member'} sent you a sign-in link for Twelve Stones.`
              : `${preview.inviterDisplayName ?? 'A family member'} invited you to Twelve Stones.`}
          </p>
        </div>

        <label className="block">
          <span className="block text-xs text-soft mb-1">Your name</span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={40}
            className="w-full h-11 px-3 rounded-xl border border-soft/30 bg-paper text-ink"
          />
        </label>

        <div className="text-xs text-soft text-center">
          Signing in as <span className="text-ink">{preview.email}</span>
        </div>

        {err && <div className="text-oxblood text-sm text-center">{err}</div>}

        <button
          onClick={accept}
          disabled={submitting || displayName.trim().length === 0}
          className="w-full h-11 rounded-xl bg-oxblood text-paper font-medium disabled:opacity-50"
        >
          {submitting
            ? 'Signing in…'
            : preview.mode === 'existing'
              ? 'Verify email & sign in'
              : 'Accept & sign in'}
        </button>
      </div>
    </div>
  );
}
